import { BodyText, Modal, Stack } from '@kvdbil/components';
import React, { useState } from 'react';
import { useHideContactButtonEffect } from '~/App/shared/hooks/useHideContactButtonHook';
import {
  LeasingPartnerPresentationFragment,
  LeasingPromotionCardFragment
} from '~/config/generated/graphql';
import { isAxiosError } from '~/helpers/axiosUtils';
import { getAllErrorMessages } from '~/helpers/errorUtils';
import { requestErrorHandler } from '~/helpers/notifyError';
import { submitLeasingInterest } from '~/helpers/orchestration/partnerLeasing';
import { useTranslation } from '~/Locale';
import LeasingInterestConfirmation from './LeasingInterestConfirmation';
import LeasingInterestForm, {
  LeasingInterestFormValues
} from './LeasingInterestForm';
import LeasingPromotionSummary from './LeasingPromotionSummary';

const contentStyles = {
  width: 'calc(100% - 2rem)',
  maxWidth: '37.5rem',
  maxHeight: 'calc(100% - 2rem)',
  borderRadius: '0.5rem'
};

export type LeasingInterestCopy = Pick<
  LeasingPartnerPresentationFragment,
  | 'interestModalTitle'
  | 'interestModalIntro'
  | 'priceFootnote'
  | 'interestConsentLabel'
  | 'interestSubmitButtonText'
  | 'interestConfirmationTitle'
  | 'interestConfirmationText'
  | 'interestConfirmationSubjectHeading'
  | 'interestConfirmationDetailsHeading'
>;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  selectedCard: LeasingPromotionCardFragment;
  leasingProvider: LeasingPartnerPresentationFragment['leasingProvider'];
  copy: LeasingInterestCopy;
};

export const LeasingPartnerModal = ({
  isOpen,
  onClose,
  selectedCard,
  leasingProvider,
  copy
}: Props) => {
  useHideContactButtonEffect();
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<string>();
  const [submittedValues, setSubmittedValues] =
    useState<LeasingInterestFormValues | null>(null);

  const priceFootnote = copy.priceFootnote ?? undefined;
  const sendFailedMessage = t('PartnerLeasing-send-failed');

  const getSubmitErrorMessage = (error: unknown) => {
    if (!isAxiosError(error) || error.response?.status !== 400) {
      return sendFailedMessage;
    }

    return (
      getAllErrorMessages(error.response.data?.message, []).join(', ') ||
      sendFailedMessage
    );
  };

  const handleSubmit = async (values: LeasingInterestFormValues) => {
    const { fullName, email, phoneNumber } = values;
    setSubmitError(undefined);

    if (!leasingProvider) {
      console.error(
        `Leasing interest cannot be sent: leasing provider is missing for card ${selectedCard.id}`
      );
      setSubmitError(sendFailedMessage);
      return;
    }

    try {
      await submitLeasingInterest(leasingProvider, {
        fullName,
        email,
        phoneNumber,
        recordId: selectedCard.id
      });
      setSubmittedValues(values);
    } catch (error: unknown) {
      console.error('Failed to send leasing interest', error);
      requestErrorHandler(error);
      setSubmitError(getSubmitErrorMessage(error));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      withCloseIcon={false}
      withPortal
      onClose={onClose}
      title={
        submittedValues ? undefined : (copy.interestModalTitle ?? undefined)
      }
      contentStyles={contentStyles}
    >
      {submittedValues ? (
        <LeasingInterestConfirmation
          selectedCard={selectedCard}
          priceFootnote={priceFootnote}
          submittedValues={submittedValues}
          onClose={onClose}
          title={copy.interestConfirmationTitle}
          text={copy.interestConfirmationText}
          subjectHeading={copy.interestConfirmationSubjectHeading}
          detailsHeading={copy.interestConfirmationDetailsHeading}
        />
      ) : (
        <Stack rowGap="1rem">
          <LeasingPromotionSummary
            {...selectedCard}
            priceFootnote={priceFootnote}
          />

          {copy.interestModalIntro && (
            <BodyText>{copy.interestModalIntro}</BodyText>
          )}

          <LeasingInterestForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            submitError={submitError}
            consentLabel={copy.interestConsentLabel}
            submitButtonText={copy.interestSubmitButtonText}
          />
        </Stack>
      )}
    </Modal>
  );
};
